import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { queryClient } from '../../app/query-client'
import { logout } from '../../mia-auth/api/auth-api'
import { useAuthStore } from '../../mia-auth/store/auth-store'
import { adminRoutes } from '../api/admin-routes'
import { apiRequest } from '../api/api-client'
import type { Page } from '../api/types'
import { MiaBrand } from '../components/MiaBrand'
import { appConfig } from '../config/app-config'
import { navigationSections, navigationTrail, visibleNavigation, type NavigationNode, type NavigationSection } from '../navigation/brain-navigation'

type TenantOption = { id: string; code: string; name: string; status: string; readinessScore: number }
type EnvironmentOption = { id: string; code: string; name: string; status: string; isDefault: boolean }
type ExpandedState = Record<string, boolean>

type ContextSelectorProps = {
  tenantId: string
  environmentId: string
  tenantOptions: TenantOption[]
  environmentOptions: EnvironmentOption[]
  onTenantChange: (tenantId: string) => void
  onEnvironmentChange: (environmentId: string) => void
  compact?: boolean
}

function ContextSelector({ tenantId, environmentId, tenantOptions, environmentOptions, onTenantChange, onEnvironmentChange, compact = false }: ContextSelectorProps) {
  return <div className={compact ? 'context-selector context-selector-compact' : 'context-selector'}>
    <label><span>Tenant</span><select value={tenantId} onChange={(event) => onTenantChange(event.target.value)}><option value="">Visão global</option>{tenantOptions.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name} · {tenant.status}</option>)}</select></label>
    <label><span>Ambiente</span><select value={environmentId} disabled={!tenantId} onChange={(event) => onEnvironmentChange(event.target.value)}><option value="">Selecione</option>{environmentOptions.map((environment) => <option key={environment.id} value={environment.id}>{environment.name}</option>)}</select></label>
  </div>
}

function Breadcrumbs({ tenant, sections }: { tenant?: TenantOption; sections: NavigationSection[] }) {
  const location = useLocation()
  const labels = useMemo(() => {
    const trail = navigationTrail(location.pathname, sections)
    if (trail.length > 0) {
      return [trail[0], tenant?.name, ...trail.slice(1)].filter((label): label is string => Boolean(label))
    }
    const path = location.pathname.replace(/^\/admin\/?/, '')
    return path ? ['MIA Enterprise AI', tenant?.name, path].filter((label): label is string => Boolean(label)) : ['Plataforma', 'Dashboard']
  }, [location.pathname, sections, tenant?.name])
  return <nav className="context-breadcrumbs" aria-label="Breadcrumb">{labels.map((label, index) => <span key={`${label}-${index}`}>{index > 0 && <b>/</b>}{label}</span>)}</nav>
}

function containsPath(node: NavigationNode, pathname: string): boolean {
  if (node.to && (pathname === node.to || pathname.startsWith(`${node.to}/`))) {
    return true
  }
  return node.children?.some((child) => containsPath(child, pathname)) ?? false
}

function activeParents(sections: NavigationSection[], pathname: string) {
  const ids = new Set<string>()
  const walk = (node: NavigationNode) => {
    if (node.children?.some((child) => containsPath(child, pathname))) {
      ids.add(node.id)
      node.children.forEach(walk)
    }
  }
  sections.forEach((section) => {
    if (section.nodes.some((node) => containsPath(node, pathname))) {
      ids.add(section.id)
    }
    section.nodes.forEach(walk)
  })
  return ids
}

function NavigationNodeView({ node, depth, expanded, onToggle, onNavigate }: { node: NavigationNode; depth: number; expanded: ExpandedState; onToggle: (id: string) => void; onNavigate: () => void }) {
  const location = useLocation()
  const hasChildren = Boolean(node.children?.length)
  const open = expanded[node.id] ?? containsPath(node, location.pathname)
  const active = containsPath(node, location.pathname)
  return <div className={`nav-node nav-node-depth-${depth}`}>
    {node.to && !hasChildren && <NavLink to={node.to} end={node.to === '/admin'} onClick={onNavigate} className={({ isActive }) => isActive ? 'nav-item nav-item-active' : 'nav-item'}><span aria-hidden="true">{node.icon}</span>{node.label}</NavLink>}
    {hasChildren && node.to && <div className={active ? 'nav-group-row nav-group-row-active' : 'nav-group-row'}><NavLink to={node.to} onClick={onNavigate} className="nav-group-link"><span aria-hidden="true">{node.icon}</span><strong>{node.label}</strong></NavLink><button type="button" className="nav-group-toggle" aria-expanded={open} aria-label={`${open ? 'Recolher' : 'Expandir'} ${node.label}`} onClick={() => onToggle(node.id)}>{open ? '−' : '+'}</button></div>}
    {hasChildren && !node.to && <button type="button" className={active ? 'nav-group-button nav-group-button-active' : 'nav-group-button'} aria-expanded={open} onClick={() => onToggle(node.id)}><span aria-hidden="true">{node.icon}</span><strong>{node.label}</strong><b>{open ? '−' : '+'}</b></button>}
    {hasChildren && open && <div className="nav-children">{node.children?.map((child) => <NavigationNodeView key={child.id} node={child} depth={depth + 1} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />)}</div>}
  </div>
}

function Navigation({ sections, expanded, onToggle, onNavigate }: { sections: NavigationSection[]; expanded: ExpandedState; onToggle: (id: string) => void; onNavigate: () => void }) {
  const location = useLocation()
  return <nav>{sections.map((section) => {
    const open = expanded[section.id] ?? section.nodes.some((node) => containsPath(node, location.pathname))
    return <section className="nav-section" key={section.id}>
      <button type="button" className={section.nodes.some((node) => containsPath(node, location.pathname)) ? 'nav-section-toggle nav-section-toggle-active' : 'nav-section-toggle'} aria-expanded={open} onClick={() => onToggle(section.id)}><span>{section.label}</span><b>{open ? '−' : '+'}</b></button>
      {open && <div className="nav-section-content">{section.nodes.map((node) => <NavigationNodeView key={node.id} node={node} depth={0} expanded={expanded} onToggle={onToggle} onNavigate={onNavigate} />)}</div>}
    </section>
  })}</nav>
}

function loadExpandedState(): ExpandedState {
  try {
    const stored = window.localStorage.getItem('mia.navigation.expanded')
    return stored ? JSON.parse(stored) as ExpandedState : { platform: true, brain: true, applications: true, 'every-styles': true }
  } catch {
    return { platform: true, brain: true, applications: true, 'every-styles': true }
  }
}

export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [expanded, setExpanded] = useState<ExpandedState>(loadExpandedState)
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const tenantId = useAuthStore((state) => state.tenantId)
  const environmentId = useAuthStore((state) => state.environmentId)
  const setTenantId = useAuthStore((state) => state.setTenantId)
  const setEnvironmentId = useAuthStore((state) => state.setEnvironmentId)
  const reset = useAuthStore((state) => state.reset)
  const navigate = useNavigate()
  const permissions = user?.permissions ?? []
  const sections = useMemo(() => visibleNavigation(navigationSections, permissions), [permissions])
  const isGlobalAdmin = permissions.includes('*')
  const tenantIds = user?.tenantIds ?? []
  const tenantOptionsQuery = useQuery({
    queryKey: ['shell', 'tenant-options'],
    queryFn: () => apiRequest<Page<TenantOption>>(`${adminRoutes.tenants.collection}?page=1&pageSize=100`),
    enabled: isGlobalAdmin,
    staleTime: 60_000,
  })
  const tenantOptions = isGlobalAdmin ? tenantOptionsQuery.data?.items ?? [] : tenantIds.map((id) => ({ id, code: id.slice(0, 8), name: id.slice(0, 8), status: 'ACTIVE', readinessScore: 0 }))
  const environmentsQuery = useQuery({
    queryKey: ['shell', 'environments', tenantId],
    queryFn: () => apiRequest<{ items: EnvironmentOption[] }>(adminRoutes.tenants.environments(tenantId), { tenantId }),
    enabled: Boolean(tenantId),
    staleTime: 60_000,
  })
  const environments = environmentsQuery.data?.items ?? []
  const selectedTenant = tenantOptions.find((tenant) => tenant.id === tenantId)
  const selectedEnvironment = environments.find((environment) => environment.id === environmentId)
  const logoutMutation = useMutation({ mutationFn: logout, onSettled: () => { queryClient.clear(); reset(); navigate('/admin/login', { replace: true }) } })

  useEffect(() => {
    if (!tenantId) {
      setEnvironmentId('')
      return
    }
    if (environments.length > 0 && !environments.some((item) => item.id === environmentId)) {
      setEnvironmentId(environments.find((item) => item.isDefault)?.id ?? environments.at(0)?.id ?? '')
    }
  }, [environmentId, environments, setEnvironmentId, tenantId])

  useEffect(() => {
    const parents = activeParents(sections, location.pathname)
    if (parents.size === 0) {
      return
    }
    setExpanded((current) => {
      const next = { ...current }
      parents.forEach((id) => { next[id] = true })
      return next
    })
  }, [location.pathname, sections])

  useEffect(() => {
    window.localStorage.setItem('mia.navigation.expanded', JSON.stringify(expanded))
  }, [expanded])

  useEffect(() => {
    if (!menuOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const close = (event: KeyboardEvent) => event.key === 'Escape' && setMenuOpen(false)
    window.addEventListener('keydown', close)
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', close) }
  }, [menuOpen])

  const handleTenantChange = (nextTenantId: string) => {
    setTenantId(nextTenantId)
    void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'auth' })
  }
  const handleEnvironmentChange = (nextEnvironmentId: string) => {
    setEnvironmentId(nextEnvironmentId)
    void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== 'auth' && query.queryKey[0] !== 'shell' })
  }
  const toggleExpanded = (id: string) => setExpanded((current) => ({ ...current, [id]: !(current[id] ?? false) }))

  return <div className="app-shell">
    <a className="skip-link" href="#main-content">Pular para o conteúdo</a>
    <header className="mobile-header"><button className="icon-button" type="button" onClick={() => setMenuOpen(true)} aria-label="Abrir menu">☰</button><MiaBrand compact product="Enterprise AI" className="mobile-brand" /><span className="environment-badge">{selectedEnvironment?.code ?? appConfig.environment.toUpperCase()}</span></header>
    <aside id="mia-main-navigation" className={menuOpen ? 'sidebar sidebar-open' : 'sidebar'} aria-label="Navegação principal">
      <div className="sidebar-brand"><MiaBrand product="Enterprise AI" /><button className="sidebar-close" type="button" onClick={() => setMenuOpen(false)} aria-label="Fechar menu">×</button></div>
      <ContextSelector compact tenantId={tenantId} environmentId={environmentId} tenantOptions={tenantOptions} environmentOptions={environments} onTenantChange={handleTenantChange} onEnvironmentChange={handleEnvironmentChange} />
      <Navigation sections={sections} expanded={expanded} onToggle={toggleExpanded} onNavigate={() => setMenuOpen(false)} />
      <div className="sidebar-footer"><div className="user-summary"><div className="avatar">{user?.name?.slice(0, 1).toUpperCase() ?? 'M'}</div><div><strong>{user?.name}</strong><small>{user?.email}</small></div></div><button className="logout-button" type="button" disabled={logoutMutation.isPending} onClick={() => logoutMutation.mutate()}>{logoutMutation.isPending ? 'Saindo' : 'Sair'}</button></div>
    </aside>
    {menuOpen && <button className="sidebar-overlay" type="button" onClick={() => setMenuOpen(false)} aria-label="Fechar menu" />}
    <main className="workspace" id="main-content">
      <div className="topbar"><div><div className="operation-status"><span className="system-dot" /> Operação online</div><div className="context-health">{selectedTenant ? `${selectedTenant.status} · Readiness ${Math.round(selectedTenant.readinessScore)}%` : 'Visão global da plataforma'}</div></div><ContextSelector tenantId={tenantId} environmentId={environmentId} tenantOptions={tenantOptions} environmentOptions={environments} onTenantChange={handleTenantChange} onEnvironmentChange={handleEnvironmentChange} /></div>
      <div className="workspace-content"><Breadcrumbs tenant={selectedTenant} sections={sections} /><Outlet /></div>
    </main>
  </div>
}
